import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "../../components/ui/button";
import { fetchMonthAvailability, DateAvailability } from "../../api/appointment";

interface CalendarProps {
  mode?: "single";
  selected?: Date;
  onSelect?: (date: Date | undefined) => void;
  className?: string;
  disabled?: (date: Date) => boolean;
  therapistId?: string;
}

const Calendar = ({
  mode = "single",
  selected,
  onSelect,
  className = "",
  disabled,
  therapistId,
}: CalendarProps) => {
  const [currentDate, setCurrentDate] = React.useState(new Date());
  const [availability, setAvailability] = React.useState<DateAvailability[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Fetch availability for current month
  React.useEffect(() => {
    const fetchAvailability = async () => {
      if (!therapistId) return;

      const startDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const endDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);

      try {
        setIsLoading(true);
        setError(null);
        const data = await fetchMonthAvailability(therapistId, startDate, endDate);
        setAvailability(data);
      } catch (err) {
        console.error('Error fetching availability:', err);
        setError(err instanceof Error ? err.message : 'Error fetching availability');
      } finally {
        setIsLoading(false);
      }
    };

    fetchAvailability();
  }, [currentDate, therapistId]);

  // Get the first day of the month
  const firstDayOfMonth = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth(),
    1
  );

  // Get the last day of the month
  const lastDayOfMonth = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth() + 1,
    0
  );

  // Get the day of the week for the first day (0 = Sunday, 1 = Monday, etc.)
  const firstDayOfWeek = firstDayOfMonth.getDay();

  // Calculate the total number of days to display (including padding)
  const totalDays = lastDayOfMonth.getDate();
  
  // Create array of weeks
  const weeks: (Date | null)[][] = [];
  let currentWeek: (Date | null)[] = [];
  
  // Add empty days for the start of the month
  for (let i = 0; i < firstDayOfWeek; i++) {
    currentWeek.push(null);
  }

  // Add all days of the month
  for (let day = 1; day <= totalDays; day++) {
    if (currentWeek.length === 7) {
      weeks.push(currentWeek);
      currentWeek = [];
    }
    currentWeek.push(
      new Date(currentDate.getFullYear(), currentDate.getMonth(), day)
    );
  }

  // Add empty days for the end of the month
  while (currentWeek.length < 7) {
    currentWeek.push(null);
  }
  weeks.push(currentWeek);

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      if (direction === 'prev') {
        newDate.setMonth(newDate.getMonth() - 1);
      } else {
        newDate.setMonth(newDate.getMonth() + 1);
      }
      return newDate;
    });
  };

  const isSelected = (date: Date) => {
    if (!selected) return false;
    return (
      date.getDate() === selected.getDate() &&
      date.getMonth() === selected.getMonth() &&
      date.getFullYear() === selected.getFullYear()
    );
  };

  const isDisabled = (date: Date) => {
    if (disabled && disabled(date)) return true;

    // Convert date to string format used by API
    const dateStr = date.toISOString().split('T')[0];
    
    // Check if we have availability data for this date
    const availabilityData = availability.find(a => a.date === dateStr);
    
    // If we don't have data, consider it unavailable
    if (!availabilityData) return true;
    
    // If we have data, check if there are slots available
    return !availabilityData.hasSlots;
  };

  const weekDays = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
  const monthNames = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];

  return (
    <div className={`w-full max-w-sm p-4 ${className}`}>
      {/* Calendar header */}
      <div className="flex items-center justify-between mb-4">
        <Button
          variant="ghost"
          onClick={() => navigateMonth('prev')}
          className="p-2"
          disabled={isLoading}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <h2 className="font-semibold">
          {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
        </h2>
        <Button
          variant="ghost"
          onClick={() => navigateMonth('next')}
          className="p-2"
          disabled={isLoading}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Week days header */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {weekDays.map((day) => (
          <div
            key={day}
            className="text-center text-sm font-medium text-gray-500"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className={`grid grid-cols-7 gap-1 ${isLoading ? 'opacity-50' : ''}`}>
        {weeks.flat().map((date, index) => {
          if (!date) {
            return <div key={`empty-${index}`} className="h-9" />;
          }

          const isToday = date.toDateString() === new Date().toDateString();
          const dateDisabled = isDisabled(date);
          
          // Check if date has available slots
          const dateStr = date.toISOString().split('T')[0];
          const dateAvailability = availability.find(a => a.date === dateStr);
          const hasAvailableSlots = dateAvailability?.hasSlots;

          return (
            <Button
              key={date.toISOString()}
              onClick={() => !dateDisabled && onSelect?.(date)}
              disabled={dateDisabled}
              variant={isSelected(date) ? "default" : "ghost"}
              className={`h-9 w-full p-0 font-normal ${
                isToday ? "bg-gray-100" : ""
              } ${
                isSelected(date) ? "bg-[#00c4cc] text-white hover:bg-[#00c4cc]" : ""
              } ${
                dateDisabled ? "text-gray-300 cursor-not-allowed bg-gray-50" : ""
              } ${
                hasAvailableSlots && !isSelected(date) && !dateDisabled ? "text-[#00c4cc] hover:bg-[#00c4cc] hover:bg-opacity-10" : ""
              }`}
            >
              {date.getDate()}
            </Button>
          );
        })}
      </div>

      {error && (
        <p className="text-red-500 text-sm mt-2 text-center">{error}</p>
      )}
    </div>
  );
};

export { Calendar };